import { createInertiaApp, router, type ResolvedComponent } from '@inertiajs/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { PostHogProvider } from '@posthog/react'
import { markStaff } from '@/lib/staff-marker'

// Local dev and test hosts send nothing to PostHog, so testing never mixes
// with real client traffic in the shared project.
const isLocalHost = /^(localhost|127\.0\.0\.1|\[::1\])$|\.localhost$|\.test$/.test(window.location.hostname)

const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2026-01-30',
  opt_out_capturing_by_default: isLocalHost,
  // ?fluent_staff=1 testing marker on every event (lib/staff-marker.ts).
  before_send: markStaff,
} as const

// Every page is its own chunk. Without `eager`, the glob returns one loader per
// page, so a visitor downloads the entrypoint, the shared chunks, and the chunk
// of the page they open. The admin, the List editor, the Action Plan, and the
// charts stay off the public pages.
const pages = import.meta.glob<{ default: ResolvedComponent }>('../pages/**/*.tsx')

// Failed page chunk reload.
//
// The deploy case is already covered by Inertia. config/initializers/inertia_rails.rb
// sets `config.version = ViteRuby.digest`, so after a deploy a stale tab's next
// in-app visit gets a 409 from the server and Inertia does a full location visit
// before `resolve` is ever called. Back/forward on a version mismatch goes
// through `missingHistoryItem`, which re-visits and hits the same 409.
//
// This handler is a second net for a page chunk import that fails for other
// reasons: a dropped mobile connection, a proxy or cache serving a stale
// manifest, a CDN blip. The import then rejects (Chrome: "Failed to fetch
// dynamically imported module"; Safari: "Importing a module script failed").
// The fix is one full load of the page the visitor asked for. A sessionStorage
// flag, cleared when a page resolves, stops a loop on a chunk that is genuinely
// broken: that case reloads once and then fails in the open.
//
// Inertia's `resolve` only gets the component name, not the URL. In Inertia
// 2.3.12 an in-app visit fires `beforeUpdate` with the final page object (url
// included) right before it calls `resolve` (core Response.setPage), so the
// listener below records that URL. On the first load and on back/forward
// (popstate, which resolves without `beforeUpdate`) the browser already sits on
// the right URL, so window.location.href is the fallback.
const RELOAD_FLAG = 'fluent:stale-chunk-reload'
let pendingVisitUrl: string | null = null
// Set right before the recovery navigation. The never-settling promise below
// leaves Inertia's serial request queue blocked, so if the browser later
// restores this page from the back-forward cache (Back after the recovery),
// every link on it would hang. A restored page that was leaving reloads.
let leaving = false

window.addEventListener('pageshow', (event) => {
  if (event.persisted && leaving) window.location.reload()
})

router.on('beforeUpdate', (event) => {
  pendingVisitUrl = event.detail.page.url
})
router.on('finish', () => {
  pendingVisitUrl = null
})

// True only when storage is readable and no reload has happened yet. Storage
// that throws means no loop guard, so the answer is no.
function canReloadOnce(): boolean {
  try {
    return window.sessionStorage.getItem(RELOAD_FLAG) !== '1'
  } catch {
    return false
  }
}

function writeReloadFlag(on: boolean) {
  try {
    if (on) window.sessionStorage.setItem(RELOAD_FLAG, '1')
    else window.sessionStorage.removeItem(RELOAD_FLAG)
  } catch {
    // Storage blocked: nothing to clear.
  }
}

// On the initial page load nothing has rendered yet, so a failure that is not
// retried would leave an empty white page. One plain line in #app tells the
// visitor what to do. On an in-app visit the app is already on screen and
// Inertia keeps the current page, so nothing extra is needed.
function showBlankPageFallback() {
  const root = document.getElementById('app')
  if (!root || root.childNodes.length > 0) return
  const note = document.createElement('p')
  note.style.padding = '24px'
  note.style.color = 'var(--color-ink, #333)'
  note.textContent = 'This page did not load. Please refresh.'
  root.appendChild(note)
}

function recoverFromFailedPageImport(name: string, error: unknown): Promise<never> {
  if (!canReloadOnce()) {
    console.error(`Inertia page chunk failed to load twice: '${name}.tsx'`, error)
    showBlankPageFallback()
    return Promise.reject(error)
  }
  writeReloadFlag(true)
  const target = pendingVisitUrl ?? window.location.href
  console.warn(`Inertia page chunk failed to load, reloading '${target}' once`, error)
  leaving = true
  // assign() to a URL that differs only by #hash is a fragment jump, not a load.
  const withoutHash = (href: string) => href.replace(/#.*$/, '')
  if (withoutHash(new URL(target, window.location.href).href) === withoutHash(window.location.href)) {
    window.location.reload()
  } else {
    window.location.assign(target)
  }
  // The tab is leaving. A promise that never settles keeps Inertia from
  // showing an error for a page that is about to be replaced.
  return new Promise<never>(() => {})
}

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const load = pages[`../pages/${name}.tsx`]
    if (!load) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
      showBlankPageFallback()
      return Promise.reject(new Error(`Missing Inertia page component: '${name}.tsx'`))
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    // page.default.layout ||= (page: ReactNode) => (<Layout>{page}</Layout>)

    return load().then(
      (page) => {
        writeReloadFlag(false)
        return page
      },
      (error: unknown) => recoverFromFailedPageImport(name, error),
    )
  },

  setup({ el, App, props }) {
    createRoot(el).render(
      <StrictMode>
        <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={options}>
          <App {...props} />
        </PostHogProvider>
      </StrictMode>
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
